import { HttpBackend, HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { lastValueFrom, throwError } from 'rxjs';
import { AuthUtils } from '../../shared/helper/auth/auth.utils';
import { cloneDeep } from 'lodash-es';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _httpClient = inject(HttpClient);
  private _authenticated: boolean = false;
  private _user: any;
  private _httpCLinetWithoutInterceptor;
  /**
   * Constructor
   */
  constructor(handler: HttpBackend) {
    this._httpCLinetWithoutInterceptor = new HttpClient(handler);
  }

  set accessToken(token: string) {
    localStorage.setItem('accessToken', token);
  }

  get accessToken(): string {
    return localStorage.getItem('accessToken') ?? '';
  }

  set userData(data: any) {
    localStorage.setItem('userData', JSON.stringify(data));
  }

  verifyEmailOrUsername(name: string) {
    let obj = {
      username: name,
    };
    return lastValueFrom(this._httpClient.post(`${environment.BASEURLAPI}/api/account/realm`, obj));
  }
  async forgotPassword(email: string): Promise<any> {
    return await lastValueFrom(
      this._httpClient.post(
        `${environment.BASEURLAPI + environment.FORGOTPASSWORD}`,
        {
          emailAddress: email,
        },
        {
          responseType: 'text',
        }
      )
    );
  }
  async changePassword(form: any): Promise<any> {
    const credentials = {
      Username: form.controls.email.value,
      CurrentPassword: form.controls.password.value,
      NewPassword: form.controls.newPassword.value,
      ConfirmPassword: form.controls.confirmPassword.value,
    };

    return await lastValueFrom(
      this._httpClient.post(
        `${environment.BASEURLAPI + environment.CHANGEPASSWORDURL}`,
        credentials,
        { responseType: 'text' }
      )
    );
  }
  async resetPassword(tempHash: string, password: string): Promise<any> {
    let obj = { temporaryHash: tempHash, Password: password };
    return await lastValueFrom(
      this._httpClient.post(
        `${
          environment.BASEURLAPI + environment.RESETPASSWORD
        }?temporaryHash=${tempHash}&Password=${password}`,
        obj
      )
    );
  }

  async resetMobileUserPassword(tempHash: string, password: string): Promise<any> {
    let obj = { temporaryHash: tempHash, Password: password };
    return await lastValueFrom(
      this._httpClient.post(
        `${
          environment.BASEURLAPI + environment.RESETMOBILEUSERPASSWORD
        }?temporaryHash=${tempHash}&Password=${password}`,
        obj
      )
    );
  }

  async signIn(credentials: { email: string; password: string }): Promise<any> {
    // Throw error, if the user is already logged in
    if (this._authenticated) {
      return throwError('User is already logged in.');
    }

    return await lastValueFrom(
      this._httpClient.post(environment.BASEURLAPI + environment.AUTHENTICATIONURL, credentials, {
        responseType: 'text',
      })
    ).then(async (response: any) => {
      // Store the access token in the local storage
      let data = localStorage.getItem('realmResponse');
      let parseData = JSON.parse(data!);
      if (parseData.name === 'LDAP') {
        return this.decodeTokenAndReturnData(response, credentials);
      }
      localStorage.setItem(
        'tempObj',
        JSON.stringify({ email: credentials.email, token: response })
      );
      return true;
    });
  }

  async signInToAuthenticationService(credentials: { Username: string; Password: string }) {
    return await lastValueFrom(
      this._httpClient.post(
        environment.BASEURL + '/AuthenticationService/api/MobileUser',
        credentials
      )
    );
  }

  decodeTokenAndReturnData(response: any, credentials: any) {
    this.accessToken = response;
    const tokenData = this.getDecodedAccessToken(this.accessToken);
    // Set the authenticated flag to true
    this._authenticated = true;
    cloneDeep(this._user);
    // Store the user on the user service
    let obj = {
      email: credentials.email,
      id: tokenData.CustomerId,
      canLoginWMPortal: tokenData.Roles.includes('CanLoginWMPortal'),
      canLoginWorkMobile: tokenData.Roles.includes('CanLoginWorkMobile'),
      canLoginMobile: tokenData.Roles.includes('CanLoginMobile'),
      dataEditEnabled: tokenData.Roles.includes('DataEditEnabled'),
      token: response,
      changePasswordRequired:
        tokenData.IsAccountSecurityChanged == 'True' || tokenData.IsPasswordChanged == 'True'
          ? true
          : false,
    };
    let webClientObj = {
      Username: tokenData.Username,
      CustomerId: tokenData.CustomerId,
      MobileUserId: tokenData.MobileUserId,
      Token: tokenData.Token,
      Cookie_name: tokenData.Cookie_name,
    };
    let bulkEditor = {
      Username: tokenData.Username,
      FirstName: tokenData.FirstName,
      Surname: tokenData.SurName,
      CustomerId: tokenData.CustomerId,
      LoginId: tokenData.LoginId,
    };
    this.userData = { ...obj };
    // Return a new observable with the response
    return { obj, userGatewayInfo: { ...webClientObj }, bulkEditor };
  }
  /**
   * Sign out
   */
  signOut() {
    // Remove the access token from the local storage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('userData');

    // Set the authenticated flag to false
    this._authenticated = false;
  }
  getDecodedAccessToken(token: string): any {
    try {
      return AuthUtils.getTokenData(token);
    } catch (Error) {
      return null;
    }
  }

  validateUser(credentials: { email: string; password: string }) {
    return this._httpCLinetWithoutInterceptor.post(
      environment.BASEURLAPI + environment.AUTHENTICATIONURL,
      credentials,
      { responseType: 'text' }
    );
  }
  /**
   * Check the authentication status
   */
  check(): boolean {
    // Check the access token expire date
    if (AuthUtils.isTokenExpired(this.accessToken)) {
      this._authenticated = false;
      return false;
    }

    // Check the access token availability
    else if (this.accessToken) {
      return true;
    }

    // Check if the user is logged in
    else if (this._authenticated) {
      return true;
    } else {
      this._authenticated = false;
      return false;
    }

    // If the access token exists and it didn't expire, sign in using it
    // return this.signInUsingToken();
  }
  authenticateUser(token: string, email: string) {
    try {
      if (!AuthUtils.isTokenExpired(token)) {
        const tokenData = this.getDecodedAccessToken(token);
        localStorage.clear();
        // Set the authenticated flag to true
        this._authenticated = true;
        cloneDeep(this._user);
        // Store the user on the user service

        this.userData = {
          email: email,
          id: tokenData.CustomerId,
          token: token,
          changePasswordRequired: tokenData.IsAccountSecurityChanged == 'False' ? false : true,
        };
        this.accessToken = token;
        return '';
      } else {
        return 'Token is Expired';
      }
    } catch (error) {
      return 'error';
    }
  }
  async verifyAccount(tempHash: string) {
    // VerifyRegistration
    return await lastValueFrom(
      this._httpClient.get(
        `${environment.BASEURLAPI}${environment.ACCOUNTVERIFICATION}?hash=${tempHash}`
      )
    );
  }
  async EnrolSSO(obj: any): Promise<any> {
    return await lastValueFrom(
      this._httpClient.post(`${environment.BASEURLAPI}/api/account/EnrolSSO`, obj, {
        responseType: 'text',
      })
    );
  }
  async AuthenticateSSO(obj: any): Promise<any> {
    return await lastValueFrom(
      this._httpClient.post(`${environment.BASEURLAPI}/api/account/AuthenticateSSO`, obj, {
        responseType: 'text',
      })
    );
  }
}
